import { useContext, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import "../styles/webshop/webshopproducts.css";
import { AppContext } from "../AppContext";
import { Helmet } from "react-helmet-async";

export default function Products() {
    const { catalogId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const { allCatalogs } = useContext(AppContext);

    const [brand, setBrand] = useState("");            // ← NO location.state
    const [products, setProducts] = useState([]);
    const [totalPages, setTotalPages] = useState(1);

    // --- PAGE NUMBER FROM URL ---
    const params = new URLSearchParams(location.search);
    const pageFromUrl = parseInt(params.get("page"), 10) || 1;
    const [currentPage, setCurrentPage] = useState(pageFromUrl);

    const pageSize = 70;
    const [searchValue, setSearchValue] = useState("");

    // -----------------------------------------------------
    // FETCH BRAND USING catalogId (ALWAYS reliable)
    // -----------------------------------------------------
    useEffect(() => {
        if (!catalogId) return;
        if (allCatalogs.length === 0) return; // wait until loaded

        const fetchCatalog = async () => {
            let response;

            if (process.env.NODE_ENV === "production") {
                response = await fetch(`https://prodexmd.ba/api/catalogs/${catalogId}`);
            } else {
                response = await fetch(`http://localhost:5000/api/catalogs/${catalogId}`);
            }

            if (!response.ok) return;

            const catalog = await response.json();
            const cleanedName = catalog.name.replace(/^\s*\d+\)/, "").trim();
            setBrand(cleanedName); // ← ALWAYS correct
        };

        fetchCatalog();
    }, [catalogId, allCatalogs]);

    // -----------------------------------------------------
    // SYNC URL -> STATE  (pagination with back button)
    // -----------------------------------------------------
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const pageInUrl = parseInt(params.get("page"), 10) || 1;

        if (pageInUrl !== currentPage) {
            setCurrentPage(pageInUrl);
        }
    }, [location.search]);

    // -----------------------------------------------------
    // FETCH PRODUCTS
    // -----------------------------------------------------
    useEffect(() => {
        if (!brand) return;

        let url = "";

        if (process.env.NODE_ENV === "production") {
            url = `https://prodexmd.ba/api/products/?page=${currentPage}&perPage=${pageSize}&brand=${brand}`;
        } else {
            url = `http://localhost:5000/api/products/?page=${currentPage}&perPage=${pageSize}&brand=${brand}`;
        }

        if (searchValue) {
            url += searchValue;
        }

        fetch(url)
            .then((response) => {
                if (!response.ok) throw new Error("Bad response");

                let totalCount = response.headers.get("X-Total-Count");
                setTotalPages(Math.ceil(totalCount / pageSize));
                return response.json();
            })
            .then((data) => {
                let sorted = [...data.products].sort((a, b) => a.name.localeCompare(b.name));

                let cleaned = sorted.map((item) => ({
                    ...item,
                    name: item.name.replace(/^\s*\d+\)/, "").trim(),
                }));

                setProducts(cleaned);
                setTotalPages(data.pagination.totalPages);
            })
            .catch((error) => console.error("Failed to load products:", error));
    }, [brand, currentPage, searchValue]);

    // -----------------------------------------------------
    // RESTORE SCROLL POSITION ON BACK
    // -----------------------------------------------------
    useEffect(() => {
        if (products.length === 0) return;

        const savedPosition = sessionStorage.getItem("scrollPosition");
        if (savedPosition) {
            requestAnimationFrame(() => {
                window.scrollTo(0, parseInt(savedPosition, 10));
                sessionStorage.removeItem("scrollPosition");
            });
        }
    }, [products]);

    // -----------------------------------------------------
    // PAGINATION BUTTONS
    // -----------------------------------------------------
    const paginationButtons = [];
    for (let i = 1; i <= totalPages; i++) {
        paginationButtons.push(
            <li className={i === currentPage ? "page-item-active" : "page-item"} key={i}>
                <a className="page-link" href={`?page=${i}`}>
                    {i}
                </a>
            </li>
        );
    }

    return (
        <>

            <Helmet>
                <title> {brand} – Kupi kvalitetne proizvode online. Brza dostava u BIH. | Prodex</title>
                <meta
                    name="description"
                    content={`Pregledajte proizvode brenda ${brand} – releji, sklopke, elektromaterijal i više. Dostava u BIH.`}
                />
                <link rel="canonical" href={`https://prodexmd.ba/productscatalogs/${catalogId}`} />

                {products.length > 0 && (
                  <script type="application/ld+json">
                  
                    {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    "itemListElement": products.map((product, index) => ({
                        "@type": "Product",
                        "position": index + 1,
                        "name": product.name,
                        // "image": product.image1,
                        "image": `https://prodexmd.ba${product.image1}`,
                        //"description": product.description !== "pometru" ? product.description : undefined,
                         "description": product.description || "Prodex proizvod",
                        "sku": product.serialNumber,
                        "offers": {
                        "@type": "Offer",
                        "price": product.price > 0 ? product.price.toFixed(2) : undefined,
                        "priceCurrency": "BAM",
                        "availability": product.price === 0 || product.price === -1 
                            ? "https://schema.org/PreOrder" 
                            : "https://schema.org/InStock",
                        "url": `https://prodexmd.ba/products1/${product._id}`
                        }
                      }))
                    })}
                    
                </script>
                )}

                {brand && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "CollectionPage",
                        "name": brand,
                        "url": `https://prodexmd.ba/productscatalogs/${catalogId}`,
                        "description": `Pregledajte proizvode iz kataloga ${brand} – releji, sklopke, elektromaterijal i više.`
                        })}
                    </script>
                )}


             

            </Helmet>

            <div className="three-blocks-container">
                <div className="block">
                    <div className="title-filters-box1">
                        <h1 className="title-products1">{brand}</h1>
                    </div>
                </div>
            </div>

            <div className="product-grid">
                {products.map((product, index) => (
                    <div className="product-card" key={index}>
                        <ProductItem product={product} />
                    </div>
                ))}
            </div>

            <ul className="pagination">{paginationButtons}</ul>
        </>
    );
}

function ProductItem({ product }) {
    const { addToCart } = useContext(AppContext);
    const [added, setAdded] = useState(false);
    const navigate = useNavigate();

    const handleAddToCart = () => {
        addToCart(product);
        setAdded(true);
        setTimeout(() => setAdded(false), 2000);
    };

    const handleProductClick = (e) => {
        e.preventDefault();
        sessionStorage.setItem("scrollPosition", window.scrollY);
        navigate(`/products1/${product._id}`);
    };

    return (
        <div className="product-item">
            <a
                href={`/products1/${product._id}`}
                onClick={handleProductClick}
                className="product-name-link"
            >
                <h4>{product.name}</h4>

                <img
                    src={`https://prodexmd.ba${product.image1}`} 
                    // src={product.image1}
                    className="img-fluid"
                    alt={product.name}
                    style={{ height: "220px", objectFit: "contain" }}
                />
            </a>

            <hr />

            {product.description === "pometru" && <h4>{product.price}KM/1m</h4>}
            {product.description !== "pometru" &&
                product.price !== 0 &&
                product.price !== -1 && <h4>{product.price}KM</h4>}
            {product.price === 0 && <h4>samo po narudžbi</h4>}
            {product.price === -1 && <h4 className="priceNegative">cijena po upitu</h4>}

            <h4 className="sifra">Šifra: {product.serialNumber}</h4>

            <div className="product-buttons">
                <button className="add-to-cart-btn" onClick={handleAddToCart}>
                    Dodaj u korpu
                </button>
                {added && <span className="added-message">✔ Dodato u korpu</span>}
            </div>
        </div>
    );
}
